import { usePlayerDiagnostics } from "@components/SwipeList/Slot.diagnostics";
import { usePlayer, usePlayerStoreApi } from "@utils/player/core/usePlayer";
import Feed from "@components/Exclusives/Feed";
import LScreen from "@components/LScreen";
import { SetupPlaceholder } from "@components/Onboarding/SetupPlaceholder";
import { WelcomeOverlay } from "@components/Onboarding/WelcomeOverlay";
import { useWebAutoAdvanceIntro } from "@components/Onboarding/useWebAutoAdvanceIntro";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef } from "react";
import type { CatalogCollectionType } from "@utils/player";
import { getCleanedUrlParams } from "@utils/links/url";
import { EmptyScreen } from "@components/Exclusives/Playlist/EmptyScreen";
import { useReloadHandler } from "@hooks/useReloadHandler";
import { useActiveQueueType } from "@utils/player";
import { selectActiveSongId } from "@utils/player/core/selectors";

import { useMe } from "@hooks/trpc/useMe";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";

import { captureException } from "@utils/captureException";
import { track } from "@utils/rudderstack";
import { toast } from "sonner-native";
import { SentryScreenDisplay } from "@components/SentryScreenDisplay";

const FeedScreen = () => {
  const { capturePlayerBlankRenderGate } = usePlayerDiagnostics();

  const playerStore = usePlayerStoreApi();

  const { query: meQuery } = useMe();
  const onboardingStep = meQuery.data?.onboardingStep;

  useWebAutoAdvanceIntro(onboardingStep);
  // NOTE: `selectIsQueueLoading` currently maps to `isLoadingMore` (infinite-scroll fetch),
  // which causes the whole screen to re-render + show overlays during rapid swipes.
  // For the feed UI, we only want to treat the queue as "loading" when it's not initialized.
  const isForyouUninitialized = usePlayer(
    (s) => !s.foryouQueue || s.foryouQueue.items.length === 0,
  );
  const activeQueueType = useActiveQueueType();

  // Check if playlist queue is empty specifically
  const isPlaylistQueueEmpty = usePlayer(
    (s) => !s.playlistQueue || s.playlistQueue.items.length === 0,
  );

  // Shared value for UI-thread animations - avoids React re-renders for visibility
  const isForYouActive = useSharedValue(activeQueueType === "foryou" ? 1 : 0);

  // Sync shared value when activeQueueType changes
  useEffect(() => {
    isForYouActive.value = activeQueueType === "foryou" ? 1 : 0;
  }, [activeQueueType, isForYouActive]);

  // Animated styles - run on UI thread, no React re-renders
  const forYouStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: isForYouActive.value ? 0 : 5000,
      },
    ],
    // Keep opacity for smooth visuals
    opacity: isForYouActive.value ? 1 : 0,
  }));

  const playlistStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: isForYouActive.value ? 5000 : 0,
      },
    ],
    opacity: isForYouActive.value ? 0 : 1,
  }));

  const {
    collectionId: rawCollectionId,
    collectionType: rawCollectionType,
    songId: rawSongId,
    shareSource: rawShareSource,
    shareVersion: rawShareVersion,
  } = useLocalSearchParams<{
    collectionId?: string;
    collectionType?: CatalogCollectionType;
    songId?: string;
    shareSource?: string;
    shareVersion?: string;
    reload?: string;
  }>();

  const collectionId = getCleanedUrlParams(rawCollectionId);
  const collectionType = getCleanedUrlParams(rawCollectionType) as
    CatalogCollectionType | undefined;
  const songId = getCleanedUrlParams(rawSongId);
  const shareSource = getCleanedUrlParams(rawShareSource) ?? "legacy-unknown";
  const shareVersion = getCleanedUrlParams(rawShareVersion) ?? "legacy";

  useReloadHandler("FeedScreen");

  const activeSongId = usePlayer(selectActiveSongId);

  // A deep-link play is "in progress" precisely while the collection params are
  // present. They are cleared (below) once the player catches up, so `isHandling`
  // is derived from the params rather than tracked as effect-set state.
  const isHandling = !!(collectionId && collectionType && songId);

  // Kick off playback once per unique deep-link target. The ref guard replaces
  // the previous `!isHandling` state guard so the imperative play call (a side
  // effect, not a setState) runs exactly once.
  const handledDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!collectionId || !collectionType || !songId) {
      handledDeepLinkRef.current = null;
      return;
    }
    const deepLinkKey = `${collectionType}|${collectionId}|${songId}`;
    if (handledDeepLinkRef.current === deepLinkKey) return;
    handledDeepLinkRef.current = deepLinkKey;

    // Uses the store action directly (not `playerActions.playFromCollection`,
    // which swallows the result) because we must observe whether playback
    // actually started: when the deep-linked song is unavailable for this
    // recipient, `playCollection` bails without starting anything, so
    // `activeSongId === songId` never becomes true and the param-clearing
    // effect below would leave `isHandling` stuck forever. Clear the params
    // ourselves in that case.
    let cancelled = false;
    const recoverFromInvalidShare = (
      reason: "play-collection-rejected" | "play-collection-threw",
      error?: unknown,
    ) => {
      if (cancelled) return;

      const diagnosticError =
        error instanceof Error
          ? error
          : new Error("Shared song link could not be played");
      const diagnosticContext = {
        collectionId,
        collectionType,
        failureReason: reason,
        shareSource,
        shareVersion,
        songId,
      };

      captureException(diagnosticError, {
        channel: "error",
        area: "share-link",
        operation: "playback-fallback",
        fingerprint: ["share-link", "playback-fallback", reason],
        tags: {
          collection_type: collectionType,
          failure_reason: reason,
          share_source: shareSource,
          share_version: shareVersion,
        },
        extra: diagnosticContext,
      });
      track("share_link_playback_failed", diagnosticContext);

      playerStore.getState().actions.setActiveQueueType("foryou");
      router.setParams({
        collectionId: undefined,
        collectionType: undefined,
        shareSource: undefined,
        shareVersion: undefined,
        songId: undefined,
      });
      // Queue the notification after the URL state settles. On web, showing it
      // synchronously before `setParams` commits can make the route update
      // unmount the toast before it is ever painted.
      setTimeout(() => {
        toast.error("This link doesn't work", {
          description: "We've taken you back to For You.",
        });
      }, 0);
    };

    playerStore
      .getState()
      .actions.playCollection(collectionId, collectionType, songId)
      .then((started) => {
        if (!started) {
          recoverFromInvalidShare("play-collection-rejected");
        }
      })
      .catch((error) =>
        recoverFromInvalidShare("play-collection-threw", error),
      );

    return () => {
      cancelled = true;
    };
  }, [collectionId, collectionType, shareSource, shareVersion, songId]);

  // Once the player reflects the deep-linked song, clear the params. Clearing
  // them also flips `isHandling` to false. `router.setParams` is an external
  // side effect (not a setState), so it correctly lives in this effect.
  useEffect(() => {
    if (
      isHandling &&
      activeSongId === songId &&
      activeQueueType === "playlist"
    ) {
      router.setParams({
        collectionId: undefined,
        collectionType: undefined,
        shareSource: undefined,
        shareVersion: undefined,
        songId: undefined,
      });
    }
  }, [activeQueueType, activeSongId, isHandling, songId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const state = playerStore.getState();
      const rawActiveQueueType = state.activeQueueType as
        string | null | undefined;
      const validQueueType =
        rawActiveQueueType === "foryou" || rawActiveQueueType === "playlist"
          ? rawActiveQueueType
          : null;
      const activeQueue =
        validQueueType === "playlist"
          ? state.playlistQueue
          : validQueueType === "foryou"
            ? state.foryouQueue
            : null;
      const activeItem =
        activeQueue && activeQueue.currentIndex >= 0
          ? activeQueue.items[activeQueue.currentIndex]
          : undefined;
      const extra = {
        activeItemExists: Boolean(activeItem),
        activeItemQueueItemId: activeItem?.qid ?? null,
        activeItemSongId: activeItem?.metadata.id ?? null,
        activeQueueType,
        activeSongId,
        collectionId: collectionId ?? null,
        collectionType: collectionType ?? null,
        isForyouUninitialized,
        isHandling,
        isPlaylistQueueEmpty,
        onboardingStep: onboardingStep ?? null,
        rawActiveQueueType: rawActiveQueueType ?? null,
        songId: songId ?? null,
      };

      if (!state._hasHydrated) {
        capturePlayerBlankRenderGate({
          extra,
          queueType: validQueueType ?? "none",
          reason: "player-store-not-hydrated",
          surface: "feed-screen",
        });
        return;
      }

      if (!validQueueType) {
        capturePlayerBlankRenderGate({
          extra,
          queueType: "none",
          reason: "active-queue-type-invalid",
          surface: "feed-screen",
        });
        return;
      }

      if (isHandling) {
        capturePlayerBlankRenderGate({
          extra,
          queueType: validQueueType,
          reason: "home-deeplink-handling-stuck",
          surface: "feed-screen",
        });
        return;
      }

      if (onboardingStep === "setup_pending") return;
      // An empty playlist queue renders EmptyScreen, a designed state.
      if (validQueueType === "playlist" && isPlaylistQueueEmpty) return;

      if (!activeQueue || activeQueue.items.length === 0) {
        capturePlayerBlankRenderGate({
          extra,
          queueType: validQueueType,
          reason: "home-active-queue-empty",
          surface: "feed-screen",
        });
        return;
      }

      if (!activeItem) {
        capturePlayerBlankRenderGate({
          extra,
          queueType: validQueueType,
          reason: "home-active-current-item-missing",
          surface: "feed-screen",
        });
      }
    }, 3_000);

    return () => clearTimeout(timeout);
  }, [
    activeQueueType,
    activeSongId,
    collectionId,
    collectionType,
    isForyouUninitialized,
    isHandling,
    isPlaylistQueueEmpty,
    onboardingStep,
    songId,
  ]);

  const isSetup = onboardingStep === "setup_pending";
  const isWelcome =
    onboardingStep === "welcome_pending" ||
    onboardingStep === "welcome_import_pending";
  const isFullyDisplayed =
    !meQuery.isPending &&
    !isHandling &&
    (isSetup ||
      isWelcome ||
      activeQueueType === "playlist" ||
      !isForyouUninitialized);

  // Show empty screen only when playlist queue is empty and we're viewing playlist
  if (isPlaylistQueueEmpty && activeQueueType === "playlist" && !isHandling) {
    return (
      <>
        <SentryScreenDisplay ready={isFullyDisplayed} />
        <EmptyScreen />
      </>
    );
  }

  return (
    <LScreen paddingHorizontal="zero">
      <SentryScreenDisplay ready={isFullyDisplayed} />
      {/* ForYou Feed - always mounted, visibility controlled by UI thread */}
      <Animated.View
        key="foryou-feed"
        style={[
          {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          forYouStyle,
        ]}
        pointerEvents={activeQueueType === "foryou" ? "auto" : "none"}
      >
        {isSetup ? (
          <SetupPlaceholder />
        ) : (
          <Feed
            queueType="foryou"
            isLoading={isForyouUninitialized || isHandling}
            loadingBg={isHandling ? "screenBg" : undefined}
          />
        )}
      </Animated.View>

      {/* Playlist Feed - always mounted, visibility controlled by UI thread */}
      <Animated.View
        key="playlist-feed"
        style={[
          {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          playlistStyle,
        ]}
        pointerEvents={activeQueueType === "playlist" ? "auto" : "none"}
      >
        <Feed
          queueType="playlist"
          isLoading={isHandling}
          loadingBg={isHandling ? "screenBg" : undefined}
        />
      </Animated.View>

      {/* Welcome overlay — renders over feed with pointerEvents="box-none" */}
      {isWelcome && <WelcomeOverlay />}
    </LScreen>
  );
};

export default FeedScreen;
